// src/api/vendorApi.js
import axiosInstance from './axiosConfig';

export const vendorApi = {
  // ============================================================
  // DASHBOARD
  // ============================================================
  getStats: async () => {
    const response = await axiosInstance.get('/vendor/stats');
    return response.data;
  },

  // ============================================================
  // PRODUCTS
  // ============================================================
  getProducts: async (params = {}) => {
    const response = await axiosInstance.get('/vendor/products', { params });
    return response.data;
  },

  createProduct: async (data) => {
    const response = await axiosInstance.post('/vendor/products', data);
    return response.data;
  },

  updateProduct: async (id, data) => {
    const response = await axiosInstance.put(`/vendor/products/${id}`, data);
    return response.data;
  },

  deleteProduct: async (id) => {
    const response = await axiosInstance.delete(`/vendor/products/${id}`);
    return response.data;
  },

  // ============================================================
  // ORDERS
  // ============================================================
  getOrders: async (params = {}) => {
    const response = await axiosInstance.get('/vendor/orders', { params });
    return response.data;
  },

  getRecentOrders: async (limit = 5) => {
    const response = await axiosInstance.get('/vendor/orders/recent', { params: { limit } });
    return response.data;
  },

  updateOrderStatus: async (id, status) => {
    const response = await axiosInstance.put(`/vendor/orders/${id}/status`, { status });
    return response.data;
  },

  getOrderDetails: async (id) => {
    const response = await axiosInstance.get(`/orders/${id}`);
    return response.data;
  },

  // ============================================================
  // CUSTOMERS
  // ============================================================
  getCustomers: async () => {
    const response = await axiosInstance.get('/vendor/customers');
    return response.data;
  },

  // ============================================================
  // ANALYTICS
  // ============================================================
  getAnalytics: async () => {
    const response = await axiosInstance.get('/vendor/analytics');
    return response.data;
  },

  // ============================================================
  // PROFILE
  // ============================================================
  getProfile: async () => {
    const response = await axiosInstance.get('/vendor/profile');
    return response.data;
  },

  updateProfile: async (data) => {
    const response = await axiosInstance.put('/vendor/profile', data);
    return response.data;
  },

  // ============================================================
  // PAYOUTS
  // ============================================================
  getPayouts: async () => {
    try {
      const response = await axiosInstance.get('/vendor/payouts');
      if (response.data && response.data.success === false) {
        console.warn("Backend returned error for Payouts:", response.data.message);
        return { success: false, message: response.data.message, balance: 0, history: [] };
      }
      return response.data;
    } catch (error) {
      console.error("Network error fetching payouts:", error.message);
      return {
        success: false,
        message: error.message,
        balance: 0,
        history: []
      };
    }
  },

  requestPayout: async (data) => {
    const response = await axiosInstance.post('/vendor/payouts/request', data);
    return response.data;
  },

  cancelPayout: async (payoutId) => {
    const response = await axiosInstance.delete(`/vendor/payouts/${payoutId}`);
    return response.data;
  },

  updatePayoutAmount: async (payoutId, amount) => {
    const response = await axiosInstance.put(`/vendor/payouts/${payoutId}`, { amount });
    return response.data;
  },

  // ============================================================
  // ✅ TRANSACTIONS (Vendor)
  // ============================================================
  getTransactions: async (params = {}) => {
    const response = await axiosInstance.get('/vendor/transactions', { params });
    return response.data;
  },

  getRecentTransactions: async (limit = 5) => {
    const response = await axiosInstance.get('/vendor/transactions/recent', { params: { limit } });
    return response.data;
  },

  getTransactionDetails: async (transactionId) => {
    const response = await axiosInstance.get(`/vendor/transactions/${transactionId}`);
    return response.data;
  },

  // ============================================================
  // KYC
  // ============================================================
  getKYCStatus: async () => {
    try {
      const response = await axiosInstance.get('/vendor/kyc');
      if (response.data && response.data.success === false) {
        console.warn("Backend returned error for KYC:", response.data.message);
        return { success: false, message: response.data.message, isVerified: false };
      }
      return response.data;
    } catch (error) {
      console.error("Network error fetching KYC status:", error.message);
      return {
        success: false,
        message: error.message,
        isVerified: false
      };
    }
  },

  uploadKycDocument: async (formData) => {
    const response = await axiosInstance.post('/vendor/kyc/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // ============================================================
  // SHIPPING & TAX SETTINGS
  // ============================================================
  getShippingTaxSettings: async () => {
    try {
      const response = await axiosInstance.get('/vendor/shipping-tax');
      return response.data;
    } catch (error) {
      console.error("Error fetching shipping tax settings:", error.message);
      return { 
        success: false, 
        message: error.message,
        data: {
          shippingCost: 0,
          taxRate: 0,
          freeShippingThreshold: null,
          shippingMethod: 'flat',
          logisticsCompany: 'default',
          estimatedDelivery: 3,
          logisticsDetails: null
        }
      };
    }
  },

  updateShippingTaxSettings: async (data) => {
    const response = await axiosInstance.put('/vendor/shipping-tax', data);
    return response.data;
  },

  getVendorLogisticsDetails: async (vendorId) => {
    const response = await axiosInstance.get(`/vendor/${vendorId}/logistics`);
    return response.data;
  },

  // ============================================================
  // COUPONS
  // ============================================================
  getCoupons: async () => {
    return axiosInstance.get('/coupons/vendor');
  },

  createCoupon: async (data) => {
    return axiosInstance.post('/coupons', data);
  },

  updateCoupon: async (id, data) => {
    return axiosInstance.put(`/coupons/${id}`, data);
  },

  toggleCoupon: async (id) => {
    return axiosInstance.patch(`/coupons/${id}/toggle`);
  },

  deleteCoupon: async (id) => {
    return axiosInstance.delete(`/coupons/${id}`);
  },

  // ============================================================
  // CHAT
  // ============================================================
  getChatRooms: async () => {
    const response = await axiosInstance.get('/chat/vendor/rooms');
    return response.data;
  },

  getMessages: async (roomId, params = {}) => {
    const response = await axiosInstance.get(`/chat/rooms/${roomId}/messages`, { params });
    return response.data;
  },

  sendMessage: async (roomId, message) => {
    const response = await axiosInstance.post(`/chat/rooms/${roomId}/messages`, { message });
    return response.data;
  },

  markAsRead: async (roomId) => {
    const response = await axiosInstance.put(`/chat/rooms/${roomId}/read`);
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await axiosInstance.get('/chat/unread');
    return response.data;
  },

  // ============================================================
  // SUPPORT TICKETS
  // ============================================================
  getSupportTickets: async (params = {}) => {
    const response = await axiosInstance.get('/support/vendor/tickets', { params });
    return response.data;
  },

  getSupportTicket: async (id) => {
    const response = await axiosInstance.get(`/support/vendor/tickets/${id}`);
    return response.data;
  },

  createSupportTicket: async (data) => {
    const response = await axiosInstance.post('/support/vendor/tickets', data);
    return response.data;
  },

  replySupportTicket: async (id, data) => {
    const response = await axiosInstance.post(`/support/vendor/tickets/${id}/reply`, data);
    return response.data;
  },

  closeSupportTicket: async (id) => {
    const response = await axiosInstance.put(`/support/vendor/tickets/${id}/close`);
    return response.data;
  },

  // ============================================================
  // VENDOR FAQS
  // ============================================================
  getVendorFaqs: async () => {
    const response = await axiosInstance.get('/support/vendor/faq');
    return response.data;
  },

  // ============================================================
  // VENDOR LIST
  // ============================================================
  getVendorsForChat: async () => {
    const response = await axiosInstance.get('/vendor/list');
    return response.data;
  },
};